import {
    BoltTwoTone,
    CloudOutlined,
    ElectricBolt,
    WorkspacePremium,
} from "@mui/icons-material";
import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { sum } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ChartDisplayType } from "glue/Chart/chart.constants";
import { EPCChip } from "legacyNext/components";
import { useAssetIntensity } from "legacyNext/hooks";
import { useAssetStore } from "legacyNext/store/asset-page-store";
import { currencyFormatter } from "legacyNext/utils";

import { buildEpcSummary, findStrandedYear, getValue } from "./kpi-table.utils";
import { getFormattedChartData } from "../../views/ReductionAnalysisView/reduction-analysis.utils";

type Props = {
    area: number | null;
    assetId: string;
};

export const KPITable = (props: Props) => {
    const { assetId, area } = props;

    const { t } = useTranslation();

    const { proposals } = useAssetStore();
    const { data: intensities, pathways: emissionPathway } = useAssetIntensity(
        assetId,
        ChartDisplayType.EMISSIONS
    );
    const { data: energyData, pathways: energyPathways } = useAssetIntensity(
        assetId,
        ChartDisplayType.ENERGY
    );

    const columns = [
        "",
        t("legacyNext.present", "present"),
        t("legacyNext.reductionPlan", "reduction plan"),
        t("legacyNext.targetAchieved", "target achieved"),
        t("legacyNext.investment", "investment"),
    ];

    const rows = useMemo(() => {
        const { epcSummary, reductionYear } = buildEpcSummary(proposals);

        const enabledProposals = proposals.filter(
            (proposal) => proposal.enabled && proposal.startYear
        );

        const lastEmissionIntensityYear = intensities
            .find((item) => item.proj)
            ?.date.getFullYear();

        const intensity = getValue(
            intensities,
            lastEmissionIntensityYear,
            "int"
        );
        const energyIntensity = getValue(
            energyData,
            lastEmissionIntensityYear,
            "int"
        );

        const strandedEmissionYear = findStrandedYear(
            intensities,
            emissionPathway,
            ["proj", "int"]
        );
        const strandedEnergyYear = findStrandedYear(
            energyData,
            energyPathways,
            ["proj", "int"]
        );

        const emissionIntensities =
            area && enabledProposals.length
                ? getFormattedChartData(
                      area,
                      ChartDisplayType.EMISSIONS,
                      enabledProposals,
                      intensities
                  )
                : [];
        const energyIntensities =
            area && enabledProposals.length
                ? getFormattedChartData(
                      area,
                      ChartDisplayType.ENERGY,
                      enabledProposals,
                      energyData
                  )
                : [];

        const intensityReduction = getValue(
            emissionIntensities,
            reductionYear,
            "red"
        );

        const energyReduction = getValue(
            energyIntensities,
            reductionYear,
            "red"
        );

        const percentageReductionIntensity =
            intensity &&
            intensityReduction &&
            intensity - intensityReduction !== 0
                ? ((intensity - intensityReduction) / intensity) * 100
                : null;
        const percentageReductionEnergy =
            energyIntensity &&
            energyReduction &&
            energyIntensity - energyReduction !== 0
                ? ((energyIntensity - energyReduction) / energyIntensity) * 100
                : null;

        const reductionPlanStrandedYear = findStrandedYear(
            emissionIntensities,
            emissionPathway,
            ["red"]
        );
        const reductionPlanEnergyStrandedYear = findStrandedYear(
            energyIntensities,
            energyPathways,
            ["red"]
        );

        const total = sum(epcSummary.map((item) => Number(item[3] ?? 0)));

        const epcTable = (
            <Table>
                <TableBody>
                    {epcSummary.map((item, index) => (
                        <TableRow
                            key={index}
                            sx={{
                                "& .MuiTableCell-root": {
                                    py: 1,
                                    borderBottom: "none",
                                },
                            }}
                        >
                            <TableCell>
                                <EPCChip classification={item[0]} />
                            </TableCell>
                            <TableCell>
                                <EPCChip classification={item[1]} />
                            </TableCell>
                            <TableCell>{item[2]}</TableCell>
                            <TableCell>
                                {currencyFormatter(item[3]) || "-"}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );

        return (
            <TableBody
                sx={{
                    "& .MuiTableRow-root": { verticalAlign: "text-top" },
                    "& > .MuiTableRow-root > .MuiTableCell-root:first-of-type ":
                        {
                            fontWeight: "bold",
                            fontSize: "0.9rem",
                            width: "40%",
                        },

                    "& .MuiTableCell-root": {
                        width: "15%",
                    },
                }}
            >
                <TableRow>
                    <TableCell>
                        <Box display="flex" flexDirection="column" gap={6}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                {t(
                                    "common.labels.energyIntensity",
                                    "energy intensity"
                                )}
                                <ElectricBolt fontSize="small" />
                            </Box>

                            <Box>
                                <Typography variant="inherit" fontWeight="bold">
                                    {t(
                                        "legacyNext.strandedYear",
                                        "stranded year"
                                    )}
                                </Typography>
                                <Typography variant="caption">
                                    {t(
                                        "legacyNext.energyStrandedYearDescription",
                                        "Strandings året er det år hvor ejendommen har en højere energi intensitet end CRREM Pathway for 1.5 C"
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box display="flex" flexDirection="column" gap={6}>
                            <Typography variant="inherit">
                                {energyIntensity?.toFixed(2) || "-"} kWh/m²
                            </Typography>
                            <Typography variant="inherit">
                                {strandedEnergyYear ?? "-"}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box display="flex" flexDirection="column" gap={6}>
                            <Typography variant="inherit">
                                {energyReduction?.toFixed(2) || "-"} kWh/m²
                                {percentageReductionEnergy && (
                                    <Chip
                                        size="small"
                                        sx={{ ml: 2, fontSize: "0.75rem" }}
                                        label={`${Math.abs(
                                            percentageReductionEnergy
                                        ).toFixed(1)}%`}
                                        color={
                                            percentageReductionEnergy > 0
                                                ? "success"
                                                : "error"
                                        }
                                    />
                                )}
                            </Typography>
                            <Typography variant="inherit">
                                {reductionPlanEnergyStrandedYear}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>{reductionYear || "-"}</TableCell>
                    <TableCell />
                </TableRow>
                <TableRow>
                    <TableCell>
                        <Box display="flex" flexDirection="column" gap={6}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                {t(
                                    "common.labels.emissionIntensity",
                                    "emission intensity"
                                )}
                                <CloudOutlined fontSize="small" />
                            </Box>
                            <Box>
                                <Typography variant="inherit" fontWeight="bold">
                                    {t(
                                        "legacyNext.strandedYear",
                                        "stranded year"
                                    )}
                                </Typography>
                                <Typography variant="caption">
                                    {t(
                                        "legacyNext.emissionStrandedYearDescription",
                                        "Strandings året er det år hvor ejendommen har en højere udledningsintensitet end CRREM Pathway for 1.5 C"
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box display="flex" flexDirection="column" gap={6}>
                            <Typography variant="inherit">
                                {intensity?.toFixed(2) || "-"} kg CO2e/m²
                            </Typography>
                            <Typography variant="inherit">
                                {strandedEmissionYear}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box display="flex" flexDirection="column" gap={6}>
                            <Typography variant="inherit">
                                {intensityReduction?.toFixed(2) || "-"} kg
                                CO2e/m²{" "}
                                {percentageReductionIntensity && (
                                    <Chip
                                        size="small"
                                        sx={{ ml: 2, fontSize: "0.75rem" }}
                                        label={`${Math.abs(
                                            percentageReductionIntensity
                                        ).toFixed(1)}%`}
                                        color={
                                            percentageReductionIntensity > 0
                                                ? "success"
                                                : "error"
                                        }
                                    />
                                )}
                            </Typography>
                            <Typography variant="inherit">
                                {reductionPlanStrandedYear}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>{reductionYear || "-"}</TableCell>
                    <TableCell />
                </TableRow>
                <TableRow>
                    <TableCell>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            {t("legacyNext.energyLabels", "energy labels")}
                            <BoltTwoTone fontSize="small" />
                        </Box>
                    </TableCell>
                    <TableCell colSpan={4} sx={{ px: 0 }}>
                        {epcTable}
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignContent="center"
                        >
                            {t("legacyNext.certificate", "Certificate")}
                            <WorkspacePremium fontSize="small" />
                        </Box>
                    </TableCell>

                    <TableCell>
                        <Chip label="DGNB - Bronze" />
                    </TableCell>
                    <TableCell>
                        <Chip label="DGNB - Silver" />
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>{t("common.total", "total")}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell>{currencyFormatter(total) || "-"}</TableCell>
                </TableRow>
            </TableBody>
        );
    }, [
        area,
        emissionPathway,
        energyData,
        energyPathways,
        intensities,
        proposals,
        t,
    ]);

    return (
        <Table>
            <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                    {columns.map((item, index) => (
                        <TableCell key={index}>
                            <Typography
                                variant="h5"
                                color="white"
                                fontWeight="bold"
                            >
                                {item}
                            </Typography>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            {rows}
        </Table>
    );
};
